import React from "react";
import { useNavigate } from "react-router-dom";
import {
  PrimaryButton,
  DefaultButton,
  mergeStyleSets,
} from "@fluentui/react";
import { Flask } from "@phosphor-icons/react";

import { useAuth } from "../hooks/useAuth";
import { accessService } from "../services/accessService";
import Footer from "../components/layout/Footer";

/* ── Palette — inspired by boroondara.vic.gov.au ── */
const p = {
  bg: "#FFFFFF",
  surface: "#F7F7F5",
  border: "#E0DDD8",
  borderLight: "#EBEBEB",
  text: "#1B1B1B",
  text2: "#3E3E3E",
  text3: "#6E6E6E",
  text4: "#A0A0A0",
  green: "#2e873d",
  greenDark: "#1F6A2C",
  greenDeep: "#174F20",
  greenLight: "#E8F5E9",
  greenPale: "#F2FAF3",
};

/* ── Styles ── */
const s = mergeStyleSets({
  page: { background: p.bg, minHeight: "100vh" },

  /* Header */
  header: {
    position: "sticky" as const,
    top: 0,
    zIndex: 50,
    background: p.bg,
    borderBottom: `1px solid ${p.border}`,
  },
  headerInner: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "0 24px",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "@media (max-width: 768px)": { padding: "0 16px", height: 48 },
  } as any,
  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    textDecoration: "none",
  },
  brandTitle: { fontSize: 14, color: p.text, fontWeight: 600 },
  headerRight: { display: "flex", alignItems: "center", gap: 10 },
  devBadge: {
    padding: "3px 10px",
    borderRadius: 4,
    background: "#FFF8E1",
    border: "1px solid #F0E0A0",
    color: "#8B6914",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase" as const,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },

  /* Hero */
  hero: {
    background: p.greenDeep,
    padding: "72px 0 64px",
    position: "relative" as const,
    "@media (max-width: 768px)": { padding: "48px 0 40px" },
  } as any,
  heroInner: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "0 24px",
    "@media (max-width: 768px)": { padding: "0 16px" },
  } as any,
  heroPre: {
    fontSize: 13,
    fontWeight: 500,
    color: "rgba(255,255,255,0.55)",
    letterSpacing: "0.04em",
    lineHeight: "18px",
    marginBottom: 20,
  },
  heroH1: {
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: 42,
    lineHeight: "50px",
    letterSpacing: "-0.03em",
    maxWidth: 640,
    marginBottom: 24,
    "@media (max-width: 768px)": { fontSize: 28, lineHeight: "36px" },
  } as any,
  heroP: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 17,
    lineHeight: "28px",
    maxWidth: 560,
    marginBottom: 40,
    "@media (max-width: 768px)": { fontSize: 15, lineHeight: "24px" },
  } as any,
  heroBtns: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap" as const,
    marginBottom: 56,
  },

  /* Stats row */
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    borderTop: "1px solid rgba(255,255,255,0.12)",
    "@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
  } as any,
  stat: {
    padding: "28px 0",
    borderRight: "1px solid rgba(255,255,255,0.08)",
    selectors: { "&:last-child": { borderRight: "none" } },
    "@media (max-width: 768px)": {
      padding: "20px 0",
      selectors: { "&:nth-child(2)": { borderRight: "none" } },
    },
  } as any,
  statNum: {
    color: "#FFFFFF",
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    lineHeight: "34px",
    marginBottom: 6,
  },
  statLabel: {
    color: "rgba(255,255,255,0.45)",
    fontSize: 13,
    fontWeight: 500,
    lineHeight: "18px",
  },

  /* Sections */
  section: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "56px 24px 0",
    "@media (max-width: 768px)": { padding: "36px 16px 0" },
  } as any,
  sectionLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: p.green,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    marginBottom: 8,
  },
  sectionH2: {
    fontSize: 26,
    fontWeight: 700,
    color: p.text,
    letterSpacing: "-0.02em",
    lineHeight: "32px",
    marginBottom: 10,
  },
  sectionP: {
    fontSize: 15,
    color: p.text3,
    lineHeight: "24px",
    maxWidth: 520,
    marginBottom: 28,
  },

  /* Capabilities grid */
  capGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 1,
    background: p.borderLight,
    border: `1px solid ${p.borderLight}`,
    borderRadius: 6,
    overflow: "hidden",
    "@media (max-width: 900px)": { gridTemplateColumns: "repeat(2, 1fr)" },
    "@media (max-width: 600px)": { gridTemplateColumns: "1fr" },
  } as any,
  capCell: {
    background: p.bg,
    padding: "24px 24px 20px",
    minHeight: 140,
    transition: "background 0.15s ease",
    selectors: { "&:hover": { background: p.surface } },
  },
  capTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: p.text,
    lineHeight: "20px",
    marginBottom: 8,
  },
  capDesc: {
    fontSize: 13,
    color: p.text3,
    lineHeight: "20px",
    marginBottom: 12,
  },
  capStatus: {
    fontSize: 11,
    fontWeight: 600,
  },
  statusLive: { color: p.green },
  statusSoon: { color: "#9A7B00" },
  statusActive: { color: "#6A5ACD" },

  /* Architecture doc cell (link) */
  docCell: {
    display: "block",
    background: p.bg,
    padding: "24px 24px 20px",
    minHeight: 140,
    textDecoration: "none",
    transition: "background 0.15s ease",
    selectors: { "&:hover": { background: p.surface } },
  },
  docArrow: {
    fontSize: 11,
    fontWeight: 600,
    color: p.green,
  },

  /* Trust row */
  trustGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 1,
    background: p.borderLight,
    border: `1px solid ${p.borderLight}`,
    borderRadius: 6,
    overflow: "hidden",
    "@media (max-width: 900px)": { gridTemplateColumns: "repeat(2, 1fr)" },
    "@media (max-width: 600px)": { gridTemplateColumns: "1fr" },
  } as any,
  trustCell: {
    background: p.bg,
    padding: "24px 20px",
    minHeight: 100,
    transition: "background 0.15s ease",
    selectors: { "&:hover": { background: p.surface } },
  },
  trustTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: p.text,
    lineHeight: "20px",
    marginBottom: 8,
  },
  trustBody: {
    fontSize: 13,
    color: p.text3,
    lineHeight: "20px",
  },

  /* CTA bar */
  ctaBar: {
    maxWidth: 1120,
    margin: "56px auto 0",
    padding: "0 24px",
    "@media (max-width: 768px)": { margin: "36px auto 0", padding: "0 16px" },
  } as any,
  ctaBarInner: {
    background: p.greenDeep,
    borderRadius: 8,
    padding: "48px 40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 32,
    "@media (max-width: 768px)": {
      flexDirection: "column",
      padding: "36px 24px",
      textAlign: "center",
    },
  } as any,
  ctaText: {
    color: "#FFFFFF",
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    lineHeight: 1.3,
    maxWidth: 420,
  },
  ctaRight: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-end",
    gap: 10,
    flexShrink: 0,
    "@media (max-width: 768px)": { alignItems: "center" },
  } as any,
  ctaLink: {
    color: "rgba(255,255,255,0.6)",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    background: "none",
    border: "none",
    textDecoration: "underline",
    fontFamily: "inherit",
    transition: "color 0.15s",
    selectors: { "&:hover": { color: "#FFFFFF" } },
  },

  footerGap: {
    height: 56,
    "@media (max-width: 768px)": { height: 36 },
  } as any,
});

/* ── Data ── */
const capabilities = [
  {
    title: "AI Domain Agents",
    desc: "Ask questions in natural language and get answers backed by live council data. Available for Finance, Census and more.",
    status: "Live" as const,
  },
  {
    title: "Self-Serve Datasets",
    desc: "Browse governed datasets, preview tables, check data quality and download as CSV. All access-controlled.",
    status: "Live" as const,
  },
  {
    title: "Dashboard Builder",
    desc: "Describe what you need and the portal builds interactive dashboards with KPIs, charts and tables.",
    status: "Live" as const,
  },
  {
    title: "Report Builder",
    desc: "Select columns, apply filters and grouping, then export custom reports from any dataset.",
    status: "Live" as const,
  },
  {
    title: "Geospatial Explorer",
    desc: "Interactive map with ward boundaries, suburb demographics and infrastructure layers.",
    status: "Live" as const,
  },
  {
    title: "Governance & Security",
    desc: "Enterprise access controls, data classification, audit trails and role-based permissions.",
    status: "Active" as const,
  },
];

const trustItems = [
  { title: "Azure AD Sign-On", body: "Authenticated through your council directory. No extra passwords." },
  { title: "Role-Based Access", body: "Granular permissions at every level. Admins control all access grants." },
  { title: "Data Classification", body: "Every dataset tagged by sensitivity. Access enforced accordingly." },
  { title: "Audit & Compliance", body: "Full logging of user activity and permission changes." },
];

const stats = [
  { num: "6", label: "Capabilities" },
  { num: "2+", label: "AI Agents" },
  { num: "5+", label: "Data Domains" },
  { num: "Enterprise", label: "Security" },
];

const platformDocs = [
  {
    title: "Enterprise architecture",
    desc: "Source systems, the Microsoft Fabric medallion (real-time and batch paths), governed datasets and consumers.",
    href: "/architecture/01-enterprise-architecture.html",
  },
  {
    title: "Data model",
    desc: "The conceptual and logical models — business entities and the seven subject-area star schemas in the gold warehouse.",
    href: "/architecture/04-logical-model.html",
  },
  {
    title: "Data quality & health",
    desc: "Live load success, freshness by source system and recent issues, straight from the warehouse.",
    href: "/architecture/06-data-quality.html",
  },
];

/* ── Buttons ── */
const heroPrimary = {
  root: {
    height: 46,
    borderRadius: 4,
    padding: "0 28px",
    background: "#FFFFFF",
    border: "none",
    color: p.greenDeep,
    fontWeight: 600 as const,
    fontSize: 14,
  },
  rootHovered: { background: "#F0F0F0", color: p.greenDeep },
};
const heroSecondary = {
  root: {
    height: 46,
    borderRadius: 4,
    padding: "0 28px",
    background: "transparent",
    color: "#FFFFFF",
    border: "1px solid rgba(255,255,255,0.25)",
    fontWeight: 500 as const,
    fontSize: 14,
  },
  rootHovered: { background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.4)" },
};
const ctaPrimary = {
  root: {
    height: 44,
    borderRadius: 4,
    padding: "0 28px",
    background: "#FFFFFF",
    border: "none",
    color: p.greenDeep,
    fontWeight: 600 as const,
    fontSize: 14,
  },
  rootHovered: { background: "#F0F0F0" },
};

function statusClass(status: string) {
  if (status === "Live") return s.statusLive;
  if (status === "Coming Soon") return s.statusSoon;
  return s.statusActive;
}

/* ──────────────────────────────────────────────── */
export default function Home() {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();
  const isDev = window.location.hostname.includes("-dev-");

  // Open a gated architecture-docs page: requires sign-in, then issues the
  // short-lived docs cookie (authenticated users only) and opens the docs.
  const openDoc = async (href: string) => {
    if (!isAuthenticated) {
      login();
      return;
    }
    const w = window.open("", "_blank");
    try {
      await accessService.post("/docs/session");
      if (w) w.location.href = href;
      else window.location.href = href;
    } catch {
      if (w) w.close();
    }
  };

  return (
    <div className={s.page}>
      {/* Header */}
      <header className={s.header}>
        <div className={s.headerInner}>
          <div className={s.brandRow} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <img src="/logo-dark.svg" alt="City of Boroondara" style={{ height: 28 }} />
            <div className={s.brandTitle}>Enterprise Data Portal</div>
          </div>
          <div className={s.headerRight}>
            {isDev && (
              <span className={s.devBadge}>
                <Flask size={10} weight="fill" />
                Dev
              </span>
            )}
            {isAuthenticated ? (
              <DefaultButton
                text="Go to Portal"
                onClick={() => navigate("/chat")}
                styles={{
                  root: { borderRadius: 4, border: `1px solid ${p.border}`, background: p.bg, color: p.text, fontSize: 13, fontWeight: 600, height: 34, padding: "0 16px" },
                  rootHovered: { background: p.surface },
                }}
              />
            ) : (
              <DefaultButton
                text="Sign In"
                onClick={login}
                styles={{
                  root: { borderRadius: 4, border: `1px solid ${p.green}`, background: p.greenLight, color: p.green, fontSize: 13, fontWeight: 600, height: 34, padding: "0 16px" },
                  rootHovered: { background: "#D4EDD4" },
                }}
              />
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className={s.hero}>
        <div className={s.heroInner}>
          <div className={s.heroPre}>City of Boroondara</div>
          <h1 className={s.heroH1}>
            Your single pane of glass for council data
          </h1>
          <p className={s.heroP}>
            A secure portal for discovering and interacting with enterprise data.
            Ask questions, build dashboards, explore maps and download reports.
          </p>

          <div className={s.heroBtns}>
            <PrimaryButton text="Get Started" onClick={() => navigate("/onboard")} styles={heroPrimary} />
            {isAuthenticated ? (
              <DefaultButton text="Go to Portal" onClick={() => navigate("/chat")} styles={heroSecondary} />
            ) : (
              <DefaultButton text="Sign In" onClick={login} styles={heroSecondary} />
            )}
          </div>

          <div className={s.statsRow}>
            {stats.map((st) => (
              <div key={st.label} className={s.stat}>
                <div className={s.statNum}>{st.num}</div>
                <div className={s.statLabel}>{st.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <div className={s.section}>
        <div className={s.sectionLabel}>Platform</div>
        <h2 className={s.sectionH2}>What you can do</h2>
        <p className={s.sectionP}>
          Everything you need to discover, explore and act on council data — no technical skills required.
        </p>

        <div className={s.capGrid}>
          {capabilities.map((cap) => (
            <div key={cap.title} className={s.capCell}>
              <div className={s.capTitle}>{cap.title}</div>
              <div className={s.capDesc}>{cap.desc}</div>
              <div className={`${s.capStatus} ${statusClass(cap.status)}`}>
                {cap.status}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trust & Security */}
      <div className={s.section}>
        <div className={s.sectionLabel}>Trust & Security</div>
        <h2 className={s.sectionH2}>Enterprise-grade from day one</h2>
        <p className={s.sectionP}>
          Built on Azure with council-grade security at every layer.
        </p>

        <div className={s.trustGrid}>
          {trustItems.map((item) => (
            <div key={item.title} className={s.trustCell}>
              <div className={s.trustTitle}>{item.title}</div>
              <div className={s.trustBody}>{item.body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Architecture & transparency */}
      <div className={s.section}>
        <div className={s.sectionLabel}>Architecture</div>
        <h2 className={s.sectionH2}>See how it works</h2>
        <p className={s.sectionP}>
          Full transparency on how the platform is built — explore the enterprise architecture,
          the data model and live data quality behind the portal.
        </p>

        <div className={s.capGrid}>
          {platformDocs.map((doc) => (
            <a
              key={doc.title}
              className={s.docCell}
              href={doc.href}
              onClick={(e) => {
                e.preventDefault();
                openDoc(doc.href);
              }}
            >
              <div className={s.capTitle}>{doc.title}</div>
              <div className={s.capDesc}>{doc.desc}</div>
              <div className={s.docArrow}>Explore &rarr;</div>
            </a>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className={s.ctaBar}>
        <div className={s.ctaBarInner}>
          <div className={s.ctaText}>
            Ready to explore your data?
          </div>
          <div className={s.ctaRight}>
            <PrimaryButton text="Get Started" onClick={() => navigate("/onboard")} styles={ctaPrimary} />
            {!isAuthenticated && (
              <button className={s.ctaLink} onClick={login}>
                Already have access? Sign in
              </button>
            )}
            {isAuthenticated && (
              <button className={s.ctaLink} onClick={() => navigate("/chat")}>
                Go to portal
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={s.footerGap} />
      <Footer />
    </div>
  );
}
